import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Search, Pencil, Trash2 } from "lucide-react";
import QuickPaymentPanel from "../components/QuickPaymentPanel";
import PosPaymentStep from "../components/PosPaymentStep";
import { useProducts } from "../context/ProductsContext";
import EditQuantityModal from "../components/EditQuantityModal";
import { AddProductModal } from "../components/AddProductModal";
import { useAuth } from "../auth/AuthContext";
import { Product } from "../types/product";
import { getAdminProducts } from "../services/productsApi";
import GlobalLoader from "../components/ui/GlobalLoader";

const POS = () => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  const quickPaymentType =
    (user?.company?.quick_payment_type as "direct" | "stylist") || "stylist";
  const [showQuickPaymentPanel, setShowQuickPaymentPanel] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<"quick" | "paynow" | null>(
    null
  );
  const [editProductId, setEditProductId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [pendingBarcode, setPendingBarcode] = useState<string>("");
  const [dismissedBarcode, setDismissedBarcode] = useState<string>("");
  const { products: cartProducts, setProducts, deleteProduct } = useProducts();

  // Fetch all available products (not just cart items)
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  // Track which product images failed to load
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  // Clear cart when POS page loads or refreshes
  // ProductsContext now handles not loading/persisting cart items, but we still clear on mount
  React.useEffect(() => {
    // Clear cart state when POS page loads
    setProducts([]);
  }, [setProducts]);

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetched = await getAdminProducts();
      setAllProducts(fetched);
    } catch (error) {
      console.error("Failed to fetch products for POS:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (user) {
      fetchProducts();
    }
  }, [user, fetchProducts]);

  // Helper function to get full image URL
  const getImageUrl = (img: any): string | null => {
    // Ensure img is a string
    if (!img) return null;
    const imgStr = typeof img === "string" ? img : String(img);
    if (!imgStr || imgStr.trim() === "") return null;

    // If already a full URL, return as is
    if (
      imgStr.startsWith("http://") ||
      imgStr.startsWith("https://") ||
      imgStr.startsWith("data:")
    ) {
      return imgStr;
    }
    // If it's a blob URL (from file preview), return as is
    if (imgStr.startsWith("blob:")) {
      return imgStr;
    }
    // If it starts with storage/ or public/, it's a storage path
    if (imgStr.startsWith("storage/") || imgStr.startsWith("public/")) {
      const baseUrl = "https://salonify.co.uk";
      const cleanPath = imgStr.startsWith("/") ? imgStr.slice(1) : imgStr;
      return `${baseUrl}/${cleanPath}`;
    }
    // If it's a relative path starting with /, prepend base URL
    if (imgStr.startsWith("/")) {
      return `https://salonify.co.uk${imgStr}`;
    }
    // Otherwise, assume it's a storage path and prepend base URL
    return `https://salonify.co.uk/storage/${imgStr}`;
  };

  // Check if search query is a 5-digit code (barcode)
  const isBarcodeSearch = useMemo(() => {
    const trimmed = searchQuery.trim();
    return /^\d{5}$/.test(trimmed);
  }, [searchQuery]);

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return allProducts;
    const query = searchQuery.toLowerCase().trim();

    // If it's a 5-digit code, search only in barcode
    if (isBarcodeSearch) {
      return allProducts.filter(
        (p) => p.barcode?.trim() === searchQuery.trim()
      );
    }

    // Regular search: name, category, brand, or barcode
    return allProducts.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query) ||
        p.brand?.toLowerCase().includes(query) ||
        p.barcode?.toLowerCase().includes(query)
    );
  }, [allProducts, searchQuery, isBarcodeSearch]);

  // Reset dismissed barcode when search query changes significantly
  useEffect(() => {
    if (searchQuery.trim().length !== 5 || !isBarcodeSearch) {
      setDismissedBarcode("");
    }
  }, [searchQuery, isBarcodeSearch]);

  // Handle barcode search with no results - open modal
  useEffect(() => {
    const trimmedBarcode = searchQuery.trim();
    if (
      isBarcodeSearch &&
      filteredProducts.length === 0 &&
      trimmedBarcode.length === 5 &&
      !showAddProductModal &&
      dismissedBarcode !== trimmedBarcode
    ) {
      setPendingBarcode(trimmedBarcode);
      setShowAddProductModal(true);
    }
  }, [
    isBarcodeSearch,
    filteredProducts.length,
    searchQuery,
    showAddProductModal,
    dismissedBarcode,
  ]);

  // Add product to cart
  const handleAddToCart = (product: Product) => {
    setProducts((prev) => {
      const existing = prev.find((p) => p.id === product.id);
      if (existing) {
        // If product already in cart, increase quantity
        return prev.map((p) =>
          p.id === product.id ? { ...p, quantity: (p.quantity ?? 1) + 1 } : p
        );
      } else {
        // Add new product to cart
        return [...prev, { ...product, quantity: 1 }];
      }
    });
  };

  // Handle product creation from modal
  const handleProductCreated = async (product: Product) => {
    // Refresh products list
    await fetchProducts();

    // Add the newly created product to cart
    handleAddToCart(product);

    // Clear search query and dismissed barcode
    setSearchQuery("");
    setDismissedBarcode("");
  };

  // Removed background color generation - using simple white background for all products

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex flex-col md:flex-row items-center gap-3 mb-6 flex-shrink-0">
        <div className="relative w-full md:flex-1 lg:flex-[0.85]">
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full border border-[#E5E0EB] rounded-md py-3 pl-4 pr-10 text-[16px] font-medium text-[#402B69] placeholder:text-[#402B69]/60 focus:outline-none shadow-sm"
          />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#885ABB]" />
        </div>
        <button
          onClick={() => setShowQuickPaymentPanel(true)}
          className="w-full md:w-auto md:flex-none lg:flex-[0.10] bg-[#22C55E] hover:bg-[#16a34a] text-white rounded-lg py-3 px-6 font-bold flex items-center justify-center gap-2 transition shadow-sm whitespace-nowrap"
        >
          <img src="/quick.svg" alt="Quick Payment" className="w-5 h-5" />
          <span>Quick Payment</span>
        </button>
      </div>

      {/* Main Grid / Stack */}
      <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 items-stretch flex-1 overflow-y-auto lg:overflow-hidden mb-4 custom-scrollbar lg:pr-0">
        {/* Left Section - Products (Order 2 on mobile, 1 on desktop) */}
        <div className="lg:col-span-2 order-2 lg:order-1 bg-white rounded-xl p-4 sm:p-6 lg:overflow-y-auto relative flex-shrink-0 lg:flex-shrink shadow-sm border border-gray-100">
          <div className="h-full">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <GlobalLoader
                  fullScreen={false}
                  compensateSidebar={false}
                  description="Loading products..."
                />
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg">No products found</p>
                {searchQuery && (
                  <p className="text-sm mt-2">Try a different search term</p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredProducts.map((product, index) => {
                  const imageUrl = getImageUrl(
                    product.image || product.images?.[0]
                  );
                  const hasImage = !!imageUrl && !brokenImages.has(product.id);

                  return (
                    <div
                      key={product.id}
                      onClick={() => handleAddToCart(product)}
                      className="bg-white border-2 border-[#E5E0EB] aspect-square rounded-md p-4 cursor-pointer overflow-hidden hover:opacity-90 transition-opacity relative"
                    >
                      {hasImage ? (
                        <div className="relative h-full w-full rounded-md overflow-hidden">
                          {/* Product image */}
                          <img
                            src={imageUrl || ""}
                            alt={product.name}
                            className="h-full w-full object-cover"
                            onError={() => {
                              // Mark this product's image as broken to show simple background
                              setBrokenImages((prev) =>
                                new Set(prev).add(product.id)
                              );
                            }}
                          />
                          {/* Gradient overlay with product info */}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-3">
                            <h3 className="text-[18px] font-bold text-white truncate">
                              {product.name}
                            </h3>
                            {product.price && (
                              <p className="text-[18px] font-extrabold text-white">
                                {currencySymbol}{product.price.toFixed(2)}
                              </p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col justify-center items-center h-full text-center">
                          <h3 className="text-[18px] font-semibold mb-2 line-clamp-2 text-[#402B69]">
                            {product.name}
                          </h3>
                          {product.price && (
                            <p className="text-[18px] font-bold text-[#402B69]">
                              {currencySymbol}{product.price.toFixed(2)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {/* Right Section - Cart (Order 1 on mobile, 2 on desktop) */}
        <div className="lg:col-span-1 order-1 lg:order-2 bg-white rounded-xl p-4 sm:p-6 flex flex-col lg:h-full overflow-hidden shadow-sm border border-gray-100 flex-shrink-0 lg:flex-shrink">
          {/* Products List (Dynamic from Context) */}
          <div className="flex-1 overflow-y-auto space-y-4 mb-6 pr-2 custom-scrollbar">
            {cartProducts.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">No products in cart</p>
                <p className="text-xs mt-1">Click on products to add them</p>
              </div>
            ) : (
              cartProducts.map((product) => (
                <div
                  key={product.id}
                  className="group flex items-center justify-between border-b border-[#E5E0EB] pb-2"
                >
                  {/* Left side: product + name */}
                  <div className="flex items-center gap-4">
                    <span className="text-[14px] text-[#402B69]">Product</span>
                    <span className="text-[15px] font-bold text-[#402B69]">
                      {product.name}
                    </span>
                    <span className="text-[13px] text-[#885ABB] ml-2">
                      x{product.quantity}
                    </span>
                  </div>

                  {/* Right side: price or icons */}
                  <span className="text-[15px] font-semibold text-[#402B69]">
                    {currencySymbol}{(product.price * (product.quantity ?? 1)).toFixed(2)}
                  </span>

                  {/* Hover Actions (Visible on touch/small screens or hover) */}
                  <div className="flex lg:hidden lg:group-hover:flex items-center gap-3 text-[#885ABB] ml-2">
                    <button
                      onClick={() => setEditProductId(product.id)}
                      className="p-1 hover:bg-[#885ABB]/10 rounded-lg transition-colors"
                      title="Edit Quantity"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteProduct(product.id)}
                      className="p-1 hover:bg-red-50 rounded-lg text-red-500 transition-colors"
                      title="Delete Item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Total + Buttons (footer) */}
          <div className="flex-shrink-0 pt-2 bg-white">
            {/* Dynamic Total Calculation */}
            <div className="flex justify-between items-center border-t border-b pb-3 border-[#E5E0EB] pt-4 mb-6">
              <span className="text-[28px] font-normal text-[#402B69]">
                Due
              </span>
              <span className="text-[28px] font-normal text-[#402B69]">
                {currencySymbol}
                {cartProducts
                  .reduce((sum, p) => sum + p.price * (p.quantity ?? 1), 0)
                  .toFixed(2)}
              </span>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => setSidebarMode("paynow")}
                className="w-full bg-[#885ABB] text-white rounded-md py-3 font-extrabold hover:bg-[#6f3fa8] transition"
              >
                PAY NOW
              </button>
            </div>
          </div>
        </div>
      </div>
      {editProductId && (
        <EditQuantityModal
          productId={editProductId}
          onClose={() => setEditProductId(null)}
        />
      )}

      {showQuickPaymentPanel && (
        <QuickPaymentPanel
          onClose={() => setShowQuickPaymentPanel(false)}
          onProceed={() => {
            setShowQuickPaymentPanel(false);
            setSidebarMode("quick");
          }}
          quickPaymentType={quickPaymentType}
        />
      )}

      {sidebarMode && (
        <PosPaymentStep
          selectedService={{
            id: "service-1",
            name: "Hair Cut",
            duration: "60 mins",
            price: `${currencySymbol}35`,
          }}
          selectedCustomer="Karen Davies"
          appointmentTime="9.30–10.30 am"
          tip={0}
          setTip={() => { }}
          goToFinalPayment={() => {
            // handle finalization
            setSidebarMode(null);
          }}
          onClose={() => {
            setSidebarMode(null);
            // Clear cart after payment
            setProducts([]);
          }}
          mode={sidebarMode}
          locationId={user?.company?.location_id || user?.location_id || 1}
          userId={1} // TODO: Get from customer selection
          onPaymentComplete={(bookingId: number | string) => {
            console.log("Payment completed, booking ID:", bookingId);
            setSidebarMode(null);
            setProducts([]);
          }}
        />
      )}

      {showAddProductModal && (
        <AddProductModal
          isOpen={showAddProductModal}
          onClose={() => {
            setShowAddProductModal(false);
            setDismissedBarcode(pendingBarcode);
            setPendingBarcode("");
            setSearchQuery("");
          }}
          onProductCreated={handleProductCreated}
          initialBarcode={pendingBarcode}
        />
      )}
    </div>
  );
};

export default POS;
